import Phaser from "phaser";
import { audioManager } from "../audio/AudioManager";
import { GAME_WIDTH, GAME_HEIGHT } from "../config";
import { gameStore, type ShipType } from "../state/GameStore";
import { SHIPS, SHIP_TEXTURES, preloadPlayerSprites } from "./game/Player";
import { Starfield } from "./game/Starfield";

const SHIP_TYPES: ShipType[] = ["VOYAGER", "DEFIANT"];

const SHIP_DISPLAY_NAMES: Record<ShipType, string> = {
  VOYAGER: "USS VOYAGER",
  DEFIANT: "USS DEFIANT",
};

const SHIP_DESCRIPTIONS: Record<ShipType, string> = {
  VOYAGER: "NCC-74656 Intrepid-class",
  DEFIANT: "NX-74205 Defiant-class",
};

// Display settings
const BOX_SIZE = 180;
const BOX_SPACING = 60;
const SELECTED_SCALE = 1.15;
const NORMAL_SCALE = 0.9;

export class ShipSelectScene extends Phaser.Scene {
  private starfield!: Starfield;
  private selectedIndex = 1; // Default to Defiant
  private shipSprites: Phaser.GameObjects.Sprite[] = [];
  private selectionBoxes: Phaser.GameObjects.Graphics[] = [];
  private nameTexts: Phaser.GameObjects.Text[] = [];
  private descTexts: Phaser.GameObjects.Text[] = [];
  private pulseTimer = 0;
  private canSelect = false;

  constructor() {
    super({ key: "ShipSelectScene" });
  }

  preload(): void {
    // Load ship sprites for display
    preloadPlayerSprites(this.load);
  }

  create(): void {
    // Ensure audio is initialized
    audioManager.init();

    // Create starfield background
    this.starfield = new Starfield(this);
    this.starfield.create(100);

    // Title
    this.add
      .text(GAME_WIDTH / 2, 60, "SELECT YOUR SHIP", {
        fontSize: "36px",
        color: "#ff9900",
        fontFamily: "Courier New",
        fontStyle: "bold",
      })
      .setOrigin(0.5);

    // Calculate positions for two ships side by side
    const totalWidth = BOX_SIZE * 2 + BOX_SPACING;
    const startX = (GAME_WIDTH - totalWidth) / 2 + BOX_SIZE / 2;
    const centerY = GAME_HEIGHT / 2 - 20;

    // Create ship displays
    for (const shipType of SHIP_TYPES) {
      const i = SHIP_TYPES.indexOf(shipType);
      const x = startX + i * (BOX_SIZE + BOX_SPACING);

      // Selection box
      const box = this.add.graphics();
      this.selectionBoxes.push(box);

      // Ship sprite - use centered origin for display (not the rotation pivot)
      const textureKey = SHIP_TEXTURES[shipType].PRISTINE;
      const shipConfig = SHIPS[shipType];
      const sprite = this.add.sprite(x, centerY, textureKey);
      const baseScale = shipConfig.scale * 2.5; // Larger for display
      sprite.setScale(baseScale * NORMAL_SCALE);
      sprite.setOrigin(0.5, 0.5); // Center for display, not rotation pivot
      this.shipSprites.push(sprite);

      // Ship name
      const nameText = this.add
        .text(x, centerY + BOX_SIZE / 2 + 20, SHIP_DISPLAY_NAMES[shipType], {
          fontSize: "18px",
          color: "#ffffff",
          fontFamily: "Courier New",
          fontStyle: "bold",
        })
        .setOrigin(0.5);
      this.nameTexts.push(nameText);

      // Ship description
      const descText = this.add
        .text(x, centerY + BOX_SIZE / 2 + 45, SHIP_DESCRIPTIONS[shipType], {
          fontSize: "12px",
          color: "#888888",
          fontFamily: "Courier New",
        })
        .setOrigin(0.5);
      this.descTexts.push(descText);

      // Make clickable
      sprite.setInteractive({ useHandCursor: true });
      sprite.on("pointerdown", () => this.selectShip(i));
      sprite.on("pointerover", () => {
        if (this.canSelect && this.selectedIndex !== i) {
          this.selectedIndex = i;
          this.updateSelection();
        }
      });
    }

    // Instructions
    this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 80, "LEFT/RIGHT to choose, ENTER to confirm", {
        fontSize: "16px",
        color: "#666666",
        fontFamily: "Courier New",
      })
      .setOrigin(0.5);

    // Update initial selection
    this.updateSelection();

    // Enable input after short delay
    this.time.delayedCall(300, () => {
      this.canSelect = true;
    });

    // Keyboard input
    this.input.keyboard?.on("keydown-LEFT", () => this.moveSelection(-1));
    this.input.keyboard?.on("keydown-RIGHT", () => this.moveSelection(1));
    this.input.keyboard?.on("keydown-A", () => this.moveSelection(-1));
    this.input.keyboard?.on("keydown-D", () => this.moveSelection(1));
    this.input.keyboard?.on("keydown-ENTER", () => this.confirmSelection());
    this.input.keyboard?.on("keydown-SPACE", () => this.confirmSelection());
  }

  update(_time: number, delta: number): void {
    // Slowly drift the starfield
    this.starfield.update(new Phaser.Math.Vector2(15, 8));

    // Update pulse timer for selection highlight
    this.pulseTimer += delta * 0.004;
    this.drawSelectionBoxes();
  }

  private moveSelection(dir: number): void {
    if (!this.canSelect) return;

    const newIndex = this.selectedIndex + dir;
    if (newIndex >= 0 && newIndex < SHIP_TYPES.length) {
      this.selectedIndex = newIndex;
      this.updateSelection();
    }
  }

  private selectShip(index: number): void {
    if (!this.canSelect) return;

    if (this.selectedIndex === index) {
      this.confirmSelection();
    } else {
      this.selectedIndex = index;
      this.updateSelection();
    }
  }

  private updateSelection(): void {
    // Update ship scales
    for (let i = 0; i < SHIP_TYPES.length; i++) {
      const shipType = SHIP_TYPES[i]!;
      const shipConfig = SHIPS[shipType];
      const baseScale = shipConfig.scale * 2.5;
      const targetScale = i === this.selectedIndex
        ? baseScale * SELECTED_SCALE
        : baseScale * NORMAL_SCALE;

      const sprite = this.shipSprites[i];
      const nameText = this.nameTexts[i];
      const descText = this.descTexts[i];

      if (sprite) {
        this.tweens.add({
          targets: sprite,
          scaleX: targetScale,
          scaleY: targetScale,
          duration: 150,
          ease: "Power2",
        });
      }

      // Update text colors
      nameText?.setColor(i === this.selectedIndex ? "#ff9900" : "#ffffff");
      descText?.setColor(i === this.selectedIndex ? "#aaaaaa" : "#666666");
    }
  }

  private drawSelectionBoxes(): void {
    const totalWidth = BOX_SIZE * 2 + BOX_SPACING;
    const startX = (GAME_WIDTH - totalWidth) / 2 + BOX_SIZE / 2;
    const centerY = GAME_HEIGHT / 2 - 20;

    for (let i = 0; i < this.selectionBoxes.length; i++) {
      const box = this.selectionBoxes[i];
      if (!box) continue;

      const x = startX + i * (BOX_SIZE + BOX_SPACING);
      const isSelected = i === this.selectedIndex;

      box.clear();

      if (isSelected) {
        // Pulsing highlight
        const pulse = Math.sin(this.pulseTimer) * 0.3 + 0.7;
        box.lineStyle(3, 0xff9900, pulse);
        box.strokeRect(
          x - BOX_SIZE / 2 - 5,
          centerY - BOX_SIZE / 2 - 5,
          BOX_SIZE + 10,
          BOX_SIZE + 10
        );

        // Inner glow
        box.fillStyle(0xff9900, 0.1 * pulse);
        box.fillRect(
          x - BOX_SIZE / 2,
          centerY - BOX_SIZE / 2,
          BOX_SIZE,
          BOX_SIZE
        );
      } else {
        // Dim border
        box.lineStyle(2, 0x444444, 0.5);
        box.strokeRect(
          x - BOX_SIZE / 2,
          centerY - BOX_SIZE / 2,
          BOX_SIZE,
          BOX_SIZE
        );
      }
    }
  }

  private confirmSelection(): void {
    if (!this.canSelect) return;
    this.canSelect = false;

    const selectedShip = SHIP_TYPES[this.selectedIndex];
    if (!selectedShip) return;

    gameStore.setSelectedShip(selectedShip);

    // Play confirmation sound
    this.playConfirmSound();

    // Flash the selected box
    this.tweens.add({
      targets: { alpha: 1 },
      alpha: 0,
      duration: 100,
      yoyo: true,
      repeat: 2,
      onUpdate: (tween) => {
        const alpha = tween.getValue() as number;
        this.drawConfirmFlash(this.selectedIndex, alpha);
      },
    });

    // Camera flash and transition
    this.cameras.main.flash(400, 255, 153, 0);

    this.time.delayedCall(500, () => {
      this.scene.start("GameScene");
    });
  }

  private drawConfirmFlash(index: number, alpha: number): void {
    const box = this.selectionBoxes[index];
    if (!box) return;

    const totalWidth = BOX_SIZE * 2 + BOX_SPACING;
    const startX = (GAME_WIDTH - totalWidth) / 2 + BOX_SIZE / 2;
    const centerY = GAME_HEIGHT / 2 - 20;
    const x = startX + index * (BOX_SIZE + BOX_SPACING);

    box.clear();
    box.fillStyle(0xffffff, alpha * 0.8);
    box.fillRect(
      x - BOX_SIZE / 2 - 5,
      centerY - BOX_SIZE / 2 - 5,
      BOX_SIZE + 10,
      BOX_SIZE + 10
    );
  }

  private playConfirmSound(): void {
    audioManager.playConfirm();
  }
}
